import { useState, useMemo, useEffect } from "react";
import { Filter, Search, ChevronDown, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useSizing } from "@/lib/sizing-store";

/**
 * CategoryTreeFilter — hierarchical category picker for the Products page.
 *
 * The tree is sourced SERVER-SIDE via `GET /api/products/categories` so a
 * 20k+ catalog doesn't ship every row to the browser just to compute a
 * category tree. The endpoint returns:
 *   { paths: [{path: "Kadın > Üst Giyim", count: 234}, ...],
 *     uncategorized: 12 }
 *
 * Refetches whenever the store bumps `productsVersion` (product added/
 * removed / re-categorised) so the popover never lies about counts.
 *
 * Selection semantics — value is an array of *full paths* (e.g.
 *   ["Kadın", "Kadın > Üst Giyim"])
 * Each selected path matches its own subtree via prefix (OR across paths):
 *   "Kadın" matches every "Kadın > ..." product; "Kadın > Üst Giyim > Bluz"
 *   matches only that exact leaf.
 *
 * Cascade behaviour (per merchant request):
 * - Selecting a node ALSO selects its *direct* children (only one level
 *   deeper — grandchildren stay untouched so the merchant keeps fine control).
 * - Un-selecting any child ALSO un-selects the parent (so a parent stays
 *   checked only while all of its direct children are checked, matching the
 *   "parent = everything under it" mental model).
 */
export const CategoryTreeFilter = ({ value, onChange, testId }) => {
    const { productsVersion } = useSizing();
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(new Set());
    const [remotePaths, setRemotePaths] = useState([]);
    const [uncategorizedCount, setUncategorizedCount] = useState(0);

    // Fetch category tree from server. Re-runs whenever the store signals
    // a product mutation via `productsVersion`.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/products/categories`);
                if (!alive) return;
                setRemotePaths(data?.paths || []);
                setUncategorizedCount(Number(data?.uncategorized || 0));
            } catch (e) {
                if (alive) {
                    setRemotePaths([]);
                    setUncategorizedCount(0);
                }
            }
        })();
        return () => { alive = false; };
    }, [productsVersion]);

    // Build the tree from server-provided paths. `path` is the full
    // breadcrumb (canonical key used for selection AND filtering). Segment
    // name is kept separately for display.
    const tree = useMemo(() => {
        const root = { children: new Map(), path: "", name: "", depth: -1, count: 0 };
        for (const { path: fullPath, count } of remotePaths) {
            const segments = fullPath.split(/[>·/]/).map((s) => s.trim()).filter(Boolean);
            let node = root;
            let acc = "";
            segments.forEach((seg, depth) => {
                acc = acc ? `${acc} > ${seg}` : seg;
                if (!node.children.has(seg)) {
                    node.children.set(seg, {
                        name: seg,
                        depth,
                        path: acc,
                        children: new Map(),
                        count: 0,
                    });
                }
                node = node.children.get(seg);
            });
            node.count = (node.count || 0) + count;
        }
        return root;
    }, [remotePaths]);

    // Search mode: flatten every node whose name matches the query.
    const flatMatches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return null;
        const out = [];
        const walk = (node) => {
            for (const child of node.children.values()) {
                if (child.name.toLowerCase().includes(q)) out.push(child);
                walk(child);
            }
        };
        walk(tree);
        return out;
    }, [tree, query]);

    const toggleExpand = (path) => {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(path)) n.delete(path); else n.add(path);
            return n;
        });
    };

    // Cascade selection — see component docblock for the two invariants.
    const toggleSelect = (node) => {
        const { path } = node;
        const childPaths = [...node.children.values()].map((c) => c.path);
        const isSelected = value.includes(path);
        if (isSelected) {
            // Deselect this + its direct children AND its parent (a parent
            // stays checked only while all direct children remain checked).
            const parentPath = path.includes(" > ")
                ? path.substring(0, path.lastIndexOf(" > "))
                : null;
            const toRemove = new Set([path, ...childPaths]);
            if (parentPath) toRemove.add(parentPath);
            onChange(value.filter((p) => !toRemove.has(p)));
        } else {
            // Select this + direct children (one level deep only). Auto-expand
            // so the merchant can see what got selected.
            const toAdd = [path, ...childPaths].filter((p) => !value.includes(p));
            onChange([...value, ...toAdd]);
            if (childPaths.length > 0) {
                setExpanded((s) => new Set(s).add(path));
            }
        }
    };

    // Indeterminate state — a parent's direct children exist and some (but not
    // all) are selected. Rendered as a partial checkbox for feedback only.
    const isIndeterminate = (node) => {
        if (node.children.size === 0) return false;
        if (value.includes(node.path)) return false;
        const childPaths = [...node.children.values()].map((c) => c.path);
        const anyChecked = childPaths.some((cp) => value.includes(cp));
        return anyChecked;
    };

    const renderNode = (node, depth) => {
        const hasChildren = node.children.size > 0;
        const isExpanded = expanded.has(node.path);
        const isSelected = value.includes(node.path);
        const partial = isIndeterminate(node);
        return (
            <div key={node.path}>
                <div
                    className={`flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted/40 cursor-pointer ${isSelected ? "bg-muted/30" : ""}`}
                    style={{ paddingLeft: `${8 + depth * 16}px` }}
                    onClick={() => toggleSelect(node)}
                    data-testid={`${testId}-node-${node.path}`}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(node.path); }}
                            className="h-4 w-4 shrink-0 grid place-items-center text-muted-foreground hover:text-foreground rounded-sm"
                            aria-label={isExpanded ? "Daralt" : "Genişlet"}
                            data-testid={`${testId}-toggle-${node.path}`}
                        >
                            <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} strokeWidth={2} />
                        </button>
                    ) : (
                        <div className="w-4 shrink-0" />
                    )}
                    <Checkbox
                        checked={isSelected ? true : (partial ? "indeterminate" : false)}
                        onCheckedChange={() => toggleSelect(node)}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0"
                    />
                    <span className="truncate flex-1">{node.name}</span>
                    {hasChildren && (
                        <span className="text-[10px] text-muted-foreground/60 font-mono tabular-nums">{node.children.size}</span>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div>
                        {[...node.children.values()].map((child) => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const active = value.length > 0;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs transition-colors ${active ? "border-foreground/40 bg-muted/60 text-foreground" : "border-border bg-surface text-muted-foreground hover:text-foreground"}`}
                    data-testid={testId}
                >
                    <Filter className="h-3 w-3" />
                    <span>Kategori</span>
                    {value.length > 0 && <span className="rounded-md bg-brand text-brand-fg px-1.5 py-0.5 text-[10px] font-mono">{value.length}</span>}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0 overflow-hidden">
                <div className="p-2 border-b border-border">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Kategori ara…" className="h-8 pl-8 text-xs bg-background" data-testid={`${testId}-search`} />
                    </div>
                </div>
                <div className="max-h-72 overflow-y-auto scroll-thin">
                    {flatMatches != null ? (
                        flatMatches.length === 0 ? (
                            <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">Sonuç yok</div>
                        ) : (
                            flatMatches.map((node) => (
                                <div
                                    key={node.path}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/40 cursor-pointer ${value.includes(node.path) ? "bg-muted/30" : ""}`}
                                    onClick={() => toggleSelect(node)}
                                    data-testid={`${testId}-flat-${node.path}`}
                                >
                                    <Checkbox checked={value.includes(node.path)} onCheckedChange={() => toggleSelect(node)} onClick={(e) => e.stopPropagation()} className="shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate">{node.name}</div>
                                        <div className="text-[10px] text-muted-foreground truncate">{node.path}</div>
                                    </div>
                                </div>
                            ))
                        )
                    ) : tree.children.size === 0 && uncategorizedCount === 0 ? (
                        <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">Kategori bulunamadı</div>
                    ) : (
                        <>
                            {[...tree.children.values()].map((node) => renderNode(node, 0))}
                            {uncategorizedCount > 0 && (
                                <div
                                    className={`flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted/40 cursor-pointer border-t border-border/60 mt-1 pt-2 ${value.includes("__uncategorized__") ? "bg-muted/30" : ""}`}
                                    style={{ paddingLeft: "8px" }}
                                    onClick={() => {
                                        if (value.includes("__uncategorized__")) {
                                            onChange(value.filter((x) => x !== "__uncategorized__"));
                                        } else {
                                            onChange([...value, "__uncategorized__"]);
                                        }
                                    }}
                                    data-testid={`${testId}-uncategorized`}
                                >
                                    <div className="w-4 shrink-0" />
                                    <Checkbox
                                        checked={value.includes("__uncategorized__")}
                                        onCheckedChange={() => {
                                            if (value.includes("__uncategorized__")) {
                                                onChange(value.filter((x) => x !== "__uncategorized__"));
                                            } else {
                                                onChange([...value, "__uncategorized__"]);
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="shrink-0"
                                    />
                                    <span className="italic text-muted-foreground flex-1">Kategorisi olmayan</span>
                                    <span className="text-[10px] text-muted-foreground/60 font-mono tabular-nums">{uncategorizedCount}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {active && (
                    <div className="border-t border-border p-2 bg-muted/20">
                        <button
                            onClick={() => onChange([])}
                            className="text-[11px] text-muted-foreground hover:text-foreground w-full text-left"
                            data-testid={`${testId}-clear`}
                        >
                            Temizle
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
